// Card da Empresa Logada - Parte inferior da Sidebar
import { useState } from 'react';
import { Building, X, MapPin, Mail, Phone, Building2 } from 'lucide-react';
import { useEmpresa } from '../../contexts/EmpresaContext';

export function CardEmpresa() {
  const { empresaSelecionada } = useEmpresa();
  const [showFullModal, setShowFullModal] = useState(false);

  // Early return AFTER all hooks
  if (!empresaSelecionada) {
    return null;
  }

  return (
    <>
      {/* Card da Empresa - Altura fixa de 64px (h-16) para alinhar com BottomHeader */}
      <div className="h-16 border-t border-gray-200/40 dark:border-gray-700/30 bg-gray-50/95 dark:bg-gray-900/30 backdrop-blur-sm flex items-center px-4">
        <div
          className="flex items-center gap-3 flex-1 px-3 py-2 rounded-lg bg-gray-100/50 dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-600/50 hover:bg-gray-200/60 dark:hover:bg-white/10 hover:shadow-lg hover:shadow-black/10 transition-all duration-200 ease-out cursor-pointer group"
          onClick={() => setShowFullModal(true)}
        >
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/30">
            <Building className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {empresaSelecionada.nomeFantasia}
            </p>
          </div>
        </div>
      </div>

      {/* Modal Full-Screen Premium */}
      {showFullModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            {/* Header Premium */}
            <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 px-8 py-8">
              <button
                onClick={() => setShowFullModal(false)}
                className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-all duration-200 group"
              >
                <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-200" />
              </button>

              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-xl">
                  <Building className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {empresaSelecionada.nomeFantasia}
                  </h2>
                  <p className="text-primary-100 text-sm">
                    Configurações da Empresa
                  </p>
                </div>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="overflow-y-auto max-h-[calc(90vh-180px)] custom-scrollbar">
              <div className="p-8 space-y-8">
                {/* Informações Básicas */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Informações Básicas
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoField label="Nome Fantasia" value={empresaSelecionada.nomeFantasia} />
                    <InfoField label="Razão Social" value={empresaSelecionada.razaoSocial} />
                    <InfoField label="CNPJ" value={empresaSelecionada.cnpj} />
                    <InfoField label="Inscrição Estadual" value={empresaSelecionada.inscricaoEstadual || 'Não informado'} />
                    <InfoField label="Inscrição Municipal" value={empresaSelecionada.inscricaoMunicipal || 'Não informado'} />
                  </div>
                </div>

                {/* Contato */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Contato
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoField label="E-mail" value={empresaSelecionada.email} icon={<Mail className="w-4 h-4" />} />
                    <InfoField label="Telefone" value={empresaSelecionada.telefone} icon={<Phone className="w-4 h-4" />} />
                  </div>
                </div>

                {/* Endereço */}
                {empresaSelecionada.endereco && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      Endereço
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoField label="CEP" value={empresaSelecionada.endereco.cep} />
                      <InfoField label="Logradouro" value={empresaSelecionada.endereco.logradouro} />
                      <InfoField label="Número" value={empresaSelecionada.endereco.numero} />
                      <InfoField label="Complemento" value={empresaSelecionada.endereco.complemento || 'Não informado'} />
                      <InfoField label="Bairro" value={empresaSelecionada.endereco.bairro} />
                      <InfoField label="Cidade" value={empresaSelecionada.endereco.cidade} />
                      <InfoField label="Estado" value={empresaSelecionada.endereco.estado} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer com Ações */}
            <div className="border-t border-gray-200 dark:border-gray-800 px-8 py-6 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowFullModal(false)}
                  className="px-6 py-2.5 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm"
                >
                  Fechar
                </button>
                <button
                  onClick={() => {
                    setShowFullModal(false);
                    window.location.href = '/app/cadastros/empresa';
                  }}
                  className="px-6 py-2.5 rounded-lg font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50"
                >
                  Editar Empresa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Componente auxiliar para exibir campos de informação
function InfoField({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="group">
      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
        {label}
      </label>
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 group-hover:border-primary-300 dark:group-hover:border-primary-700/50 transition-all duration-200">
        {icon && <span className="text-gray-400 dark:text-gray-500">{icon}</span>}
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {value}
        </span>
      </div>
    </div>
  );
}
